// =============================================================================
// MEMORY SESSION STORE (Zustand)
// =============================================================================
// Manages shared photo slideshow state during video calls

import { create } from 'zustand';
import { socketService } from '../api/socket.js';
import { api } from '../api/client.js';
import { useCallStore } from './callStore.js';
import type {
  MemorySessionState,
  MemoryPhoto,
  MemoryControlAction,
  MemorySessionDisplayConfig,
  MemoryAlbumSummary,
} from '@chlann/shared';

// =============================================================================
// TYPES
// =============================================================================

interface MemorySessionStore {
  // State
  session: MemorySessionState | null;
  photos: MemoryPhoto[];
  albums: MemoryAlbumSummary[];
  displayConfig: MemorySessionDisplayConfig;
  isLoading: boolean;
  error: string | null;

  // A pending request to co-view an On-This-Day memory. Set by
  // launchMemoryFromDate; consumed by ActiveCallView once a call is live so the
  // album picker opens automatically and the slideshow can start.
  pendingLaunch: { conversationId: string; date: string } | null;

  // Computed
  currentPhoto: MemoryPhoto | null;
  hasNextPhoto: boolean;
  hasPreviousPhoto: boolean;
  progress: number; // 0-100

  // Actions
  loadAlbums: () => Promise<void>;
  startSession: (callId: string, albumId?: string, photoIds?: string[]) => Promise<boolean>;
  startDateMemorySession: (callId: string, conversationId: string, date: string) => Promise<boolean>;
  startRecapSession: (callId: string, conversationId: string) => Promise<boolean>;
  launchMemoryFromDate: (conversationId: string, date: string) => Promise<void>;
  clearPendingLaunch: () => void;
  endSession: () => void;
  nextPhoto: () => void;
  previousPhoto: () => void;
  goToPhoto: (index: number) => void;
  toggleAutoPlay: () => void;
  setAutoPlayInterval: (seconds: number) => void;
  requestControl: () => void;
  updateDisplayConfig: (config: Partial<MemorySessionDisplayConfig>) => void;

  // Socket event handlers
  setupMemorySocketListeners: () => () => void;
  handleSessionStarted: (session: MemorySessionState, photos: MemoryPhoto[]) => void;
  handleSessionUpdated: (
    sessionId: string,
    currentIndex: number,
    isAutoPlaying: boolean,
    updatedBy: string,
    updatedByName: string
  ) => void;
  handleSessionEnded: (sessionId: string) => void;
  handleControlGranted: (sessionId: string, controllerId: string) => void;
  handleSessionSync: (session: MemorySessionState, photos: MemoryPhoto[]) => void;
  handleStartFailed: (reason: 'empty', source: 'album' | 'date' | 'recap' | 'selection') => void;

  // Internal
  clearError: () => void;
  reset: () => void;
}

// =============================================================================
// DEFAULT DISPLAY CONFIG
// =============================================================================

const defaultDisplayConfig: MemorySessionDisplayConfig = {
  videoRatio: 0.33,
  showThumbnailStrip: true,
  photoFit: 'contain',
  showPhotoInfo: true,
  controlSize: 'large',
  showProgressBar: true,
  highContrast: false,
  largeText: true,
};

// =============================================================================
// STORE
// =============================================================================

export const useMemorySessionStore = create<MemorySessionStore>()((set, get) => ({
  // Initial state
  session: null,
  photos: [],
  albums: [],
  displayConfig: defaultDisplayConfig,
  isLoading: false,
  error: null,
  pendingLaunch: null,

  // Computed values (implemented as getters in actions)
  get currentPhoto() {
    const { session, photos } = get();
    if (!session || photos.length === 0) return null;
    return photos[session.currentIndex] || null;
  },

  get hasNextPhoto() {
    const { session, photos } = get();
    if (!session) return false;
    return session.currentIndex < photos.length - 1;
  },

  get hasPreviousPhoto() {
    const { session } = get();
    if (!session) return false;
    return session.currentIndex > 0;
  },

  get progress() {
    const { session, photos } = get();
    if (!session || photos.length === 0) return 0;
    return ((session.currentIndex + 1) / photos.length) * 100;
  },

  // ---------------------------------------------------------------------------
  // Load available albums for selection
  // ---------------------------------------------------------------------------
  loadAlbums: async () => {
    set({ isLoading: true, error: null });

    try {
      const result = await api.getAlbums();

      if (result.error) {
        set({ isLoading: false, error: result.error.message });
        return;
      }

      if (result.data) {
        // Convert to MemoryAlbumSummary format
        const albums: MemoryAlbumSummary[] = result.data.albums.map((a) => ({
          id: a.id,
          name: a.name,
          coverPhotoUrl: a.coverPhoto?.thumbnailKey || a.coverPhoto?.storageKey || null,
          photoCount: a.photoCount,
          type: a.familyId ? 'family' : a.isShared ? 'shared' : 'personal',
          previewPhotos: [], // Will be loaded on demand
        }));

        set({ isLoading: false, albums });
      }
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load albums',
      });
    }
  },

  // ---------------------------------------------------------------------------
  // Start a new memory session
  // ---------------------------------------------------------------------------
  startSession: async (callId, albumId, photoIds) => {
    const socket = socketService.getSocket();
    if (!socket?.connected) {
      set({ error: 'Not connected to server' });
      return false;
    }

    set({ isLoading: true, error: null });

    try {
      // Emit socket event to start session
      socket.emit('memory:start', {
        callId,
        albumId,
        photoIds,
        autoPlayIntervalSeconds: 10,
      });

      // Session will be confirmed via socket event
      set({ isLoading: false });
      return true;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to start memory session',
      });
      return false;
    }
  },

  // ---------------------------------------------------------------------------
  // Start a date-clustered smart memory session (no album). The server
  // assembles the photo set from the conversation's image attachments shared
  // on the same calendar day in previous years.
  // ---------------------------------------------------------------------------
  startDateMemorySession: async (callId, conversationId, date) => {
    const socket = socketService.getSocket();
    if (!socket?.connected) {
      set({ error: 'Not connected to server' });
      return false;
    }

    set({ isLoading: true, error: null });

    try {
      socket.emit('memory:start', {
        callId,
        dateMemory: { conversationId, date },
        autoPlayIntervalSeconds: 10,
      });
      // Session is confirmed via the memory:started socket event.
      set({ isLoading: false });
      return true;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to start memory session',
      });
      return false;
    }
  },

  // ---------------------------------------------------------------------------
  // Start an auto-compiled recap montage (no album). The server assembles the
  // set from every image this conversation has shared and plays it through the
  // same co-view engine as a date memory.
  // ---------------------------------------------------------------------------
  startRecapSession: async (callId, conversationId) => {
    const socket = socketService.getSocket();
    if (!socket?.connected) {
      set({ error: 'Not connected to server' });
      return false;
    }

    set({ isLoading: true, error: null });

    try {
      socket.emit('memory:start', {
        callId,
        recap: { conversationId },
        autoPlayIntervalSeconds: 10,
      });
      // Session is confirmed via the memory:started socket event; an empty
      // recap comes back as memory:start-failed and is surfaced as an error.
      set({ isLoading: false });
      return true;
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to start memory session',
      });
      return false;
    }
  },

  // ---------------------------------------------------------------------------
  // Launch a co-view from an On-This-Day memory.
  // If a call is already live on this conversation, just flag the launch so the
  // call view opens the album picker. Otherwise start a call first ("Call Mam to
  // look together") - the picker opens once the call is active. The actual
  // slideshow runs off an album the way the existing co-view does; the date is
  // kept for the daily prompt and Wave-2 date-clustered smart memories.
  // ---------------------------------------------------------------------------
  launchMemoryFromDate: async (conversationId, date) => {
    set({ pendingLaunch: { conversationId, date } });

    const callStore = useCallStore.getState();
    const { activeCall } = callStore;

    // Already on a call for this conversation - the picker opens via pendingLaunch.
    if (activeCall && activeCall.conversationId === conversationId) return;

    // Already on a different call - do not interrupt it; drop the request.
    if (activeCall) {
      set({ pendingLaunch: null });
      set({ error: 'Already in another call' });
      return;
    }

    // No live call - start one so the family can look back together.
    const callId = await callStore.startCall(conversationId, 'video');
    if (!callId) {
      set({ pendingLaunch: null });
    }
  },

  clearPendingLaunch: () => { set({ pendingLaunch: null }); },

  // ---------------------------------------------------------------------------
  // End the current session
  // ---------------------------------------------------------------------------
  endSession: () => {
    const { session } = get();
    if (!session) return;

    const socket = socketService.getSocket();
    socket?.emit('memory:end', { sessionId: session.sessionId });
    set({ session: null, photos: [] });
  },

  // ---------------------------------------------------------------------------
  // Navigation controls
  // ---------------------------------------------------------------------------
  nextPhoto: () => {
    const { session, photos } = get();
    if (!session || session.currentIndex >= photos.length - 1) return;

    const socket = socketService.getSocket();
    socket?.emit('memory:control', {
      sessionId: session.sessionId,
      action: 'next',
    });
  },

  previousPhoto: () => {
    const { session } = get();
    if (!session || session.currentIndex <= 0) return;

    const socket = socketService.getSocket();
    socket?.emit('memory:control', {
      sessionId: session.sessionId,
      action: 'previous',
    });
  },

  goToPhoto: (index) => {
    const { session, photos } = get();
    if (!session || index < 0 || index >= photos.length) return;

    const socket = socketService.getSocket();
    socket?.emit('memory:control', {
      sessionId: session.sessionId,
      action: 'goto',
      payload: { index },
    });
  },

  toggleAutoPlay: () => {
    const { session } = get();
    if (!session) return;

    const action: MemoryControlAction = session.isAutoPlaying ? 'pause' : 'play';
    const socket = socketService.getSocket();
    socket?.emit('memory:control', {
      sessionId: session.sessionId,
      action,
    });
  },

  setAutoPlayInterval: (seconds) => {
    const { session } = get();
    if (!session) return;

    // Update locally (could also send to server if needed)
    set({
      session: {
        ...session,
        autoPlayIntervalSeconds: seconds,
      },
    });
  },

  requestControl: () => {
    const { session } = get();
    if (!session) return;

    const socket = socketService.getSocket();
    socket?.emit('memory:control', {
      sessionId: session.sessionId,
      action: 'request_control',
    });
  },

  updateDisplayConfig: (config) => {
    set({
      displayConfig: {
        ...get().displayConfig,
        ...config,
      },
    });
  },

  // ---------------------------------------------------------------------------
  // Setup socket event listeners
  // ---------------------------------------------------------------------------
  setupMemorySocketListeners: () => {
    const socket = socketService.getSocket();
    if (!socket) {
      return () => {};
    }

    const handleStarted = (data: { session: MemorySessionState; photos: MemoryPhoto[] }) => {
      get().handleSessionStarted(data.session, data.photos);
    };

    const handleUpdated = (data: {
      sessionId: string;
      currentIndex: number;
      isAutoPlaying: boolean;
      updatedBy: string;
      updatedByName: string;
    }) => {
      get().handleSessionUpdated(
        data.sessionId,
        data.currentIndex,
        data.isAutoPlaying,
        data.updatedBy,
        data.updatedByName
      );
    };

    const handleEnded = (data: { sessionId: string }) => {
      get().handleSessionEnded(data.sessionId);
    };

    const handleControlGranted = (data: { sessionId: string; controllerId: string }) => {
      get().handleControlGranted(data.sessionId, data.controllerId);
    };

    const handleSync = (data: { session: MemorySessionState; photos: MemoryPhoto[] }) => {
      get().handleSessionSync(data.session, data.photos);
    };

    const handleStartFailed = (data: {
      reason: 'empty';
      source: 'album' | 'date' | 'recap' | 'selection';
    }) => {
      get().handleStartFailed(data.reason, data.source);
    };

    socket.on('memory:started', handleStarted);
    socket.on('memory:updated', handleUpdated);
    socket.on('memory:ended', handleEnded);
    socket.on('memory:control-granted', handleControlGranted);
    socket.on('memory:sync', handleSync);
    socket.on('memory:start-failed', handleStartFailed);

    return () => {
      socket.off('memory:started', handleStarted);
      socket.off('memory:updated', handleUpdated);
      socket.off('memory:ended', handleEnded);
      socket.off('memory:control-granted', handleControlGranted);
      socket.off('memory:sync', handleSync);
      socket.off('memory:start-failed', handleStartFailed);
    };
  },

  // ---------------------------------------------------------------------------
  // Socket event handlers
  // ---------------------------------------------------------------------------
  handleSessionStarted: (session, photos) => {
    set({
      session,
      photos,
      isLoading: false,
      error: null,
    });
  },

  handleSessionUpdated: (sessionId, currentIndex, isAutoPlaying, _updatedBy, _updatedByName) => {
    const { session } = get();
    if (!session || session.sessionId !== sessionId) return;

    set({
      session: {
        ...session,
        currentIndex,
        isAutoPlaying,
        updatedAt: new Date().toISOString(),
      },
    });
  },

  handleSessionEnded: (sessionId) => {
    const { session } = get();
    if (!session || session.sessionId !== sessionId) return;

    set({
      session: null,
      photos: [],
    });
  },

  handleControlGranted: (sessionId, controllerId) => {
    const { session } = get();
    if (!session || session.sessionId !== sessionId) return;

    set({
      session: {
        ...session,
        participants: session.participants.map((p) => ({
          ...p,
          isController: p.userId === controllerId,
        })),
      },
    });
  },

  handleSessionSync: (session, photos) => {
    set({
      session,
      photos,
      isLoading: false,
    });
  },

  // The server could not assemble any photos for the requested source. Surface
  // a stable error code the picker maps to friendly copy with an action, so the
  // initiator is never left on a blank call screen wondering what happened.
  handleStartFailed: (_reason, source) => {
    set({ isLoading: false, error: `memory:empty:${source}` });
  },

  clearError: () => { set({ error: null }); },

  // Reset store on logout (shared-device privacy). displayConfig returns to
  // defaults too: it is in-memory user preference, not device preference.
  reset: () =>
    { set({
      session: null,
      photos: [],
      albums: [],
      displayConfig: defaultDisplayConfig,
      isLoading: false,
      error: null,
      pendingLaunch: null,
    }); },
}));
